import React from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { localize } from '@saviynt/common';
import { Box, Icon, InlineMessage } from '@saviynt/design-system';
import PropTypes from 'prop-types';

import {
  CAMPAIGN_TYPES,
  CAMPAIGNTYPE_ENTITLEMENTOWNER,
} from '../../../utils/constant';
import { useInvertedLocalize } from '../../../utils/localization';
import { certsFormattedMessage, getMessageKey } from '../../../utils/utils';
import {
  BULK_ACTION_FOOTER_OBJECTS_MESSGAE_KEYS,
  BULK_ACTION_IDS,
  getBulkActionFooterBtns,
} from '../constant';
import { invertedMessages } from '../messages';

import {
  BULK_ACTION_FOOTER_BTNS_REVERSE,
  BULK_ACTION_FOOTER_OBJECTS,
  BULK_ACTION_OBJ_USERS,
  DEFAULT_BULK_ACTION_COMMON_ACTION_PREFIX,
  EO_BULK_ACTION_COMMON_ACTION_PREFIX,
} from './constant';

function BulkActionCommonSubTitle({ id, bulkFooterStats }) {
  const intl = useIntl();
  const {
    core: { configMap, campaignType },
    lineActions: { reassignConfigMap },
  } = useSelector((state) => state?.certifications?.details);
  const { messages } = useSelector((state) => state?.user);

  const l = useInvertedLocalize(invertedMessages);
  const isEOCampaign =
    CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_ENTITLEMENTOWNER;
  const BULK_ACTION_COMMON_ACTION_PREFIX = isEOCampaign
    ? EO_BULK_ACTION_COMMON_ACTION_PREFIX
    : DEFAULT_BULK_ACTION_COMMON_ACTION_PREFIX;

  const { showSelfCertificationNote } = reassignConfigMap;

  const getSubtitleContent = () => {
    if (
      id ===
      getBulkActionFooterBtns(campaignType, reassignConfigMap).TERMINATE.id
    ) {
      if (configMap?.LOCKTASKEXPIRE === '1' || configMap?.LOCKTASKS === '1') {
        const messageKey =
          bulkFooterStats?.[BULK_ACTION_OBJ_USERS] > 1
            ? 'CERTS_BULK_TERMINATE_SUB_1'
            : 'CERTS_BULK_TERMINATE_SUB_2';

        return messages.messageKey || localize(intl, getMessageKey(messageKey));
      }

      return (
        messages.CERTS_BULK_TERMINATE_SUB_3 ||
        localize(intl, getMessageKey('CERTS_BULK_TERMINATE_SUB_3'))
      );
    }

    const buttonKey = BULK_ACTION_FOOTER_BTNS_REVERSE?.[id];
    const buttonObjectsForTotal = getBulkActionFooterBtns(
      campaignType,
      reassignConfigMap
    )[buttonKey]?.objectsToTotal;

    const tempSubtitleTextEdit1 = buttonObjectsForTotal
      ?.filter((key) => bulkFooterStats[key] > 0)
      .map((key) => {
        const messageKey =
          BULK_ACTION_FOOTER_OBJECTS_MESSGAE_KEYS[
            `${BULK_ACTION_FOOTER_OBJECTS[key]}${
              bulkFooterStats[key] > 1 ? 's' : ''
            }`
          ];

        return `${bulkFooterStats[key]} ${
          messages.messageKey || localize(intl, getMessageKey(messageKey))
        }`;
      });

    if (!tempSubtitleTextEdit1) return '';

    if (tempSubtitleTextEdit1?.length === 0) {
      return (
        messages.CERTS_BULK_NOTHING_SELECTED ||
        localize(intl, getMessageKey('CERTS_BULK_NOTHING_SELECTED'))
      );
    }

    // Handling case only for 1 type of item
    if (tempSubtitleTextEdit1?.length === 1) {
      const messageKey = BULK_ACTION_COMMON_ACTION_PREFIX?.[id];

      return certsFormattedMessage(
        messages.messageKey || localize(intl, getMessageKey(messageKey)),
        {
          count: tempSubtitleTextEdit1[0],
        }
      );
    }

    // Handling case only for different type of items
    const tempSubtitleTextEdit2 = buttonObjectsForTotal // Without userCount
      ?.filter(
        (key) => bulkFooterStats[key] > 0 && key !== BULK_ACTION_OBJ_USERS
      )
      .map((key) => {
        const messageKey =
          BULK_ACTION_FOOTER_OBJECTS_MESSGAE_KEYS[
            `${BULK_ACTION_FOOTER_OBJECTS[key]}${
              bulkFooterStats[key] > 1 ? 's' : ''
            }`
          ];

        return `${bulkFooterStats[key]} ${
          messages.messageKey || localize(intl, getMessageKey(messageKey))
        }`;
      });

    if (!bulkFooterStats?.[BULK_ACTION_OBJ_USERS]) {
      let tempPreText = tempSubtitleTextEdit2[0];

      if (tempSubtitleTextEdit2?.length > 1) {
        tempPreText = `${tempSubtitleTextEdit2
          .slice(0, tempSubtitleTextEdit2.length - 1)
          .join(', ')} and ${
          tempSubtitleTextEdit2[tempSubtitleTextEdit2.length - 1]
        }`;
      } else {
        tempPreText = `${tempPreText}`;
      }

      const messageKey = BULK_ACTION_COMMON_ACTION_PREFIX?.[id];

      return certsFormattedMessage(
        messages.messageKey || localize(intl, getMessageKey(messageKey)),
        {
          count: tempPreText,
        }
      );
    }

    const userObjCount = bulkFooterStats?.[BULK_ACTION_OBJ_USERS];
    let tempPreText = tempSubtitleTextEdit2[0];

    if (tempSubtitleTextEdit2?.length > 1) {
      tempPreText = `${tempSubtitleTextEdit2
        .slice(0, tempSubtitleTextEdit2.length - 1)
        .join(', ')} ${
        messages.CERTS_BULK_AND ||
        localize(intl, getMessageKey('CERTS_BULK_AND'))
      } ${tempSubtitleTextEdit2[tempSubtitleTextEdit2.length - 1]}`;
    } else {
      tempPreText = `${tempPreText}`;
    }

    if (
      id !==
        getBulkActionFooterBtns(campaignType, reassignConfigMap).REJECT.id ||
      id !==
        getBulkActionFooterBtns(campaignType, reassignConfigMap).DECOMMISSION.id
    ) {
      const messageKey1 =
        userObjCount > 1
          ? 'CERTS_BULK_COMMON_USERS_SUB'
          : 'CERTS_BULK_COMMON_USER_SUB';

      const messageKey2 = BULK_ACTION_COMMON_ACTION_PREFIX?.[id];

      if (
        !reassignConfigMap?.SHOWEMPLOYEESTATUSOPTIONINSTEP1?.includes('2') &&
        id ===
          getBulkActionFooterBtns(campaignType, reassignConfigMap).APPROVE.id
      ) {
        return (
          <ul className='BulkActionCommonSubTitle'>
            <li>
              {certsFormattedMessage(
                messages.messageKey2 ||
                  localize(intl, getMessageKey(messageKey2)),
                {
                  count: tempPreText,
                }
              )}
            </li>
          </ul>
        );
      }

      return (
        <ul className='BulkActionCommonSubTitle'>
          <li>
            {certsFormattedMessage(
              messages.messageKey1 ||
                localize(intl, getMessageKey(messageKey1)),
              {
                count: userObjCount,
              }
            )}
          </li>
          <li>
            {certsFormattedMessage(
              messages.messageKey2 ||
                localize(intl, getMessageKey(messageKey2)),
              {
                count: tempPreText,
              }
            )}
          </li>
        </ul>
      );
    }

    const messageKey = BULK_ACTION_COMMON_ACTION_PREFIX?.[id];

    return certsFormattedMessage(
      messages.messageKey || localize(intl, getMessageKey(messageKey)),
      {
        count: tempPreText,
      }
    );
  };

  return (
    <Box>
      {getSubtitleContent()}
      {isEOCampaign &&
        id !== BULK_ACTION_IDS.DECOMMISSION &&
        Boolean(showSelfCertificationNote) && (
          <InlineMessage
            className='BulkActionCommonSubTitle-inlineMessage'
            text={l(
              'Action will not be applied for access items that belong to the certifier.'
            )}
            colorTheme='info'
            size='medium'
            leftIcon={<Icon kind='AlertInfoFilled' />}
          />
        )}
    </Box>
  );
}

BulkActionCommonSubTitle.propTypes = {
  id: PropTypes.string.isRequired,
  bulkFooterStats: PropTypes.shape({}),
};

BulkActionCommonSubTitle.defaultProps = { bulkFooterStats: {} };

export default BulkActionCommonSubTitle;
